<template>
    <div class="view-report p-2 mb-4 export-report" id="view-report-page">
        <div class="row mx-0"> 
            <div class="p-0 w-100">
                <div 
                    v-for="section in sections" 
                    :key="section.id"
                    class="row mt-3 px-2"
                >
                    <div class="card">
                        <div class="card-body">
                            <report-view-section 
                                :page="page" 
                                :client="client" 
                                :connections="connections" 
                                :page_section="section" 
                                :applied_chart_filter="false"
                            ></report-view-section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div 
            v-for="svg in svgs" 
            :key="svg.id"
            class="row"
        >
            <div 
                class="col svg-container" 
                :id="'svg-' + svg.id" 
                :data-chart-id="svg.id" 
                v-html="svg.svg"
            ></div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Context } from "svgcanvas";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartExportMixin from '../../mixins/chart_export';

export default defineComponent({
    name: 'ExportChartsView',
    mixins: [chartExportMixin],
    props: {
        page: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        user: {
            type: Object,
            default: null
        },
        connections: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            sections: [],
            svgs: [],
            sizes: {
                FULL_WIDTH: { width: 1200, height: 450 },
                SINGLE_SMALL: { width: 750, height: 450 },
                THREE_CHARTS: { width: 400, height: 400 },
                TWO_CHARTS: { width: 600, height: 400 }
            }
        };
    },
    beforeMount() {
        const section_id = window.location.pathname.split('/').slice(-1);
        //If it is a number
        if (section_id[0].match(/^\d+$/)) {
            for (let i = 0; i < this.page.sections.length; i++) {
                if (this.page.sections[i].id === section_id[0]) {
                    this.sections.push(this.page.sections[i]);
                }
            }
        } else {
            //Just select the first section
            for (let i = 0; i < this.page.sections.length; i++) {
                this.sections.push(this.page.sections[i]);
            }
        }

            //Get the sizes array from the querystring
        const params = new URLSearchParams(window.location.search);
        const size_overrides = params.get('sizes');
        
        if (size_overrides) {
            const parsed_overrides = JSON.parse(size_overrides);
            for (const [key, value] of Object.entries(parsed_overrides)) {
                this.sizes[key] = value;
            }
        }
    },
    mounted() {
        //Resize the sections
        const containers = document.querySelectorAll('.size-container');
        
        for (let i = 0; i < containers.length; i++) {
            const container = containers[i];
            const chart = container.querySelector("canvas");
            if(!chart) continue; //If it is a table or html

            //Get the size
            const size = container.dataset.size;
            
            //Full Width
            if (size === 'x-large' || size === 'large') {
                chart.dataset.width = this.sizes.FULL_WIDTH.width;
                chart.dataset.height = this.sizes.FULL_WIDTH.height;
            } else if (size === 'small' && containers[i + 1] && containers[i + 2] && 
                containers[i + 1].dataset.size === 'small' && 
                containers[i + 2].dataset.size === 'small') {
                //If this chart and the next two are all small...
                chart.dataset.width = this.sizes.THREE_CHARTS.width;
                chart.dataset.height = this.sizes.THREE_CHARTS.height;

                //And set the next two dataset values
                containers[i + 1].querySelector("canvas").dataset.width = this.sizes.THREE_CHARTS.width;
                containers[i + 1].querySelector("canvas").dataset.height = this.sizes.THREE_CHARTS.height;
                containers[i + 2].querySelector("canvas").dataset.width = this.sizes.THREE_CHARTS.width;
                containers[i + 2].querySelector("canvas").dataset.height = this.sizes.THREE_CHARTS.height;

                i += 2;
            } else if (size === 'small') {
                chart.dataset.width = this.sizes.SINGLE_SMALL.width;
                chart.dataset.height = this.sizes.SINGLE_SMALL.height;
            } else if (size === 'medium' && containers[i + 1] && 
                      containers[i + 1].dataset.size === 'medium') {
                 //If this chart and the next is medium
                chart.dataset.width = this.sizes.TWO_CHARTS.width;
                chart.dataset.height = this.sizes.TWO_CHARTS.height;

                //And set the next two dataset values
                containers[i + 1].querySelector("canvas").dataset.width = this.sizes.TWO_CHARTS.width;
                containers[i + 1].querySelector("canvas").dataset.height = this.sizes.TWO_CHARTS.height;

                i += 1;
            } else {
                //By default, set it to the full width
                chart.dataset.width = this.sizes.FULL_WIDTH.width;
                chart.dataset.height = this.sizes.FULL_WIDTH.height;
            }
        }
        //Add the necessary modifications to canvas2svg library to work with chartjs
        Context.prototype.getContext = function(contextId) {
            if (contextId === "2d" || contextId === "2D") {
                return this;
            }
            return null;
        };
        Context.prototype.style = function() { return this.__canvas.style; };
        Context.prototype.getAttribute = function(name) { return this[name]; };
        Context.prototype.addEventListener = function(type, listener, eventListenerOptions) {};

        // Wait for Chart.js instances to be available before exporting
        this.waitForCharts().then(() => {
            this.exportCharts();
        });
    },
    methods: {
        // Wait until all canvas elements inside .chart-template have an associated Chart.js instance
        waitForCharts() {
            return new Promise(resolve => {
                const check = () => {
                    const canvases = document.querySelectorAll('.chart-template canvas');

                    if (canvases.length === 0) {
                        // No canvases found – nothing to wait for
                        return resolve();
                    }

                    const allReady = Array.from(canvases).every(c => Chart.getChart(c) || c.__chartConfigExport);

                    if (allReady) {
                        resolve();
                    } else {
                        setTimeout(check, 100); // Retry after a short delay
                    }
                };

                check();
            });
        },
        exportCharts() {
        
            //Get all
            const containers = document.querySelectorAll('.chart-template');

            //Loop through each chart and convert it to an svg
            containers.forEach(c => {
                const chart = c.querySelector("canvas");
                if(!chart) return; //If it is a table or html

                let svg = this.convertToSVG(chart);
                svg = this.updateTextElements(svg);
                
                if (c.querySelector('.line-chart-template') || c.querySelector('.bar-line-chart-template')) {
                    svg = this.addLinesToChart(svg);
                }

                this.svgs.push({
                    id: c.dataset.chartId,
                    svg: svg
                });
            });
        },
        convertToSVG(c) {
            const context = c.getContext("2d");
            let chart = Chart.getChart(c);

            // Attempt to rebuild the chart instance if it doesn't exist but we have a stored config
            if (!chart && c.__chartConfigExport) {
                try {
                    chart = new Chart(context, c.__chartConfigExport);
                } catch (e) {
                    console.warn('Could not rebuild missing chart instance for export', e);
                }
            }

            // If we still don't have a chart, abort conversion for this canvas
            if (!chart) {
                console.warn('Chart instance not found for canvas during SVG export', c);
                return '';
            }

            // Filter out hidden datasets before creating SVG
            let filteredData = this.getVisibleChartData(chart);
            
            // Apply custom colors for export (user/agency/client from page props)
            filteredData = this.applyCustomColorsForExport(filteredData);
            
            // Create export config with filtered data
            const exportConfig = {
                type: chart.config.type,
                data: filteredData,
                options: { ...chart.config.options }
            };

            // Ensure datalabels render correctly after filtering datasets
            this.wrapDatalabelFormatter(exportConfig, chart, filteredData);

            // deactivate responsiveness and animation
            exportConfig.options.responsive = false;
            exportConfig.options.animation = false;
            exportConfig.options.devicePixelRatio = 1;

            // canvas2svg 'mock' context
            const width = c.dataset.width || c.offsetWidth;
            const height = c.dataset.height || c.offsetHeight;
            const svgContext = Context({ width, height });

            // Create a temporary Chart instance on the mock context with filtered data
            const mockChart = new Chart(svgContext, {
                ...exportConfig,
                plugins: [ChartDataLabels]
            });

            let svgString = svgContext.getSerializedSvg(false);

            // Clean up to avoid Chart.js animator memory leaks
            try {
                mockChart.destroy();
            } catch (e) {
                // Ignore cleanup errors
            }

            // Normalize colors for PowerPoint/Office compatibility (converts rgb() to hex)
            svgString = this.normalizeSvgForOffice(svgString);

            return svgString;
        },
        /**
         * Wrap datalabel formatter so "last dataset" logic works with filtered datasets
         */
        wrapDatalabelFormatter(exportConfig, chart, filteredData) {
            const originalOptions = chart.config.options || {};
            const originalDatalabels = originalOptions.plugins?.datalabels;
            if (!originalDatalabels || !originalDatalabels.formatter) {
                return;
            }

            const originalFormatter = originalDatalabels.formatter;

            const safeFormatter = function(value, context) {
                const dataset = filteredData.datasets[context.datasetIndex] || {};
                const originalIndex = dataset.__originalIndex ?? context.datasetIndex;

                // Respect original visibility
                try {
                    const meta = chart.getDatasetMeta ? chart.getDatasetMeta(originalIndex) : null;
                    if (meta && (meta.hidden === true || meta.hidden === 'true')) {
                        return null;
                    }
                    if (chart.isDatasetVisible && typeof chart.isDatasetVisible === 'function') {
                        if (!chart.isDatasetVisible(originalIndex)) {
                            return null;
                        }
                    }
                } catch (e) {
                    // continue
                }

                try {
                    const fakeContext = {
                        ...context,
                        datasetIndex: originalIndex,
                        chart: {
                            ...context.chart,
                            data: chart.data
                        }
                    };
                    return originalFormatter.call(this, value, fakeContext);
                } catch (e) {
                    return null;
                }
            };

            exportConfig.options.plugins = exportConfig.options.plugins || {};
            exportConfig.options.plugins.datalabels = {
                ...originalDatalabels,
                formatter: safeFormatter
            };
        },
        /**
         * Get visible chart data by filtering out hidden datasets
         * @param {Chart} chart - The Chart.js instance
         * @returns {Object} Filtered data object with only visible datasets
         */
        getVisibleChartData(chart) {
            const currentDatasets = chart.data.datasets || [];
            
            // Filter to only include visible datasets
            const visibleDatasets = currentDatasets.filter((ds, index) => {
                // Check dataset meta for visibility state
                try {
                    const meta = chart.getDatasetMeta ? chart.getDatasetMeta(index) : null;
                    if (meta && (meta.hidden === true || meta.hidden === 'true')) {
                        return false;
                    }
                } catch (e) {
                    // If getDatasetMeta fails, continue with other checks
                }
                
                // Use Chart.js API to check if dataset is actually visible
                try {
                    if (chart.isDatasetVisible && typeof chart.isDatasetVisible === 'function') {
                        if (!chart.isDatasetVisible(index)) {
                            return false;
                        }
                    }
                } catch (e) {
                    // If isDatasetVisible fails, continue with other checks
                }
                
                // Also check the hidden property on the dataset itself
                if (ds.hidden === true || ds.hidden === 'true') {
                    return false;
                }
                
                return true;
            });
            
            // Create filtered data object and track original indices
            return {
                labels: chart.data.labels || [],
                datasets: visibleDatasets.map(ds => {
                    const cloned = JSON.parse(JSON.stringify(ds));
                    // Remove hidden property to ensure clean state
                    delete cloned.hidden;
                    cloned.__originalIndex = chart.data.datasets.indexOf(ds);
                    return cloned;
                })
            };
        },
        updateTextElements(svgString) {
              // Create a new DOMParser
            const parser = new DOMParser();

              // Parse the SVG string into an XML document
            const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

              // Get the root SVG element
            const svg = svgDoc.documentElement;

              // Find all <text> elements
            const textElements = svg.querySelectorAll('text');

            textElements.forEach(text => {
                // Check if font-size is not set or empty
                if (!text.getAttribute('font-size') || text.getAttribute('font-size') === '') {
                    text.setAttribute('font-size', '15px');
                }

                // Check if font-family is set to "DM Sans"
                if (text.getAttribute('font-family') === 'DM Sans') {
                    text.setAttribute('font-family', 'DM Sans, "Franklin Gothic Medium", Arial');
                }
            });

              // Serialize the SVG back to a string
            const serializer = new XMLSerializer();
            return serializer.serializeToString(svg);
        },
        addLinesToChart(svgString) {
              // Create a new DOMParser
            const parser = new DOMParser();

              // Parse the SVG string into an XML document
            const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

              // Get the root SVG element
            const svg = svgDoc.documentElement;

              // Find all <g> elements that contain the points
            const pointGroups = svg.querySelectorAll('g > g');

              // Loop through each group and extract points
            pointGroups.forEach(group => {
                const points = Array.from(group.querySelectorAll('path[fill]'));
                if (points.length < 2) return;

                // Build the path data
                let pathData = '';
                points.forEach((point, index) => {
                  // Extract the coordinates and radius from the d attribute
                    const coords = point.getAttribute('d').match(/M (\d+(\.\d+)?) (\d+(\.\d+)?)/);
                    const radiusMatch = point.getAttribute('d').match(/A (\d+(\.\d+)?)/);

                    if (coords && radiusMatch) {
                        const x = parseFloat(coords[1]);
                        const y = parseFloat(coords[3]);
                        const radius = parseFloat(radiusMatch[1]);

                        const centerX = x - radius;
                        const centerY = y;

                        if (index === 0) {
                            pathData += `M ${centerX} ${centerY}`;
                        } else {
                            pathData += ` L ${centerX} ${centerY}`;
                        }
                    }
                });

                // Create a new path element
                const linePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                linePath.setAttribute('d', pathData);
                linePath.setAttribute('fill', 'none');
                linePath.setAttribute('stroke', group.querySelector('path[stroke]').getAttribute('stroke'));
                linePath.setAttribute('stroke-width', '1');

                // Append the new path to the same group as the points
                group.appendChild(linePath);
            });

              // Serialize the SVG back to a string
            const serializer = new XMLSerializer();
            return serializer.serializeToString(svg);
        }
    }
});
</script>
